'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import { Brain, AlertTriangle, Activity, Zap, RefreshCw, ChevronDown, ChevronUp, Wifi, WifiOff, Loader2 } from 'lucide-react';
import { useChat } from '@/lib/hooks/useChat';
import { useSession } from './SessionProvider';

// ─── EEG Channel Signal Generator ────────────────────────────────────────────
// Generates a 2-second window of named EEG channel signals (256 Hz × 2s = 512 samples/channel)
// matching the same format used by LiveEEGIndicator for /process-eeg
function generateNamedChannelBatch(sampleRate = 256, windowSeconds = 2) {
  const numSamples = sampleRate * windowSeconds;
  const channelNames = ['Fp1', 'Fp2', 'F3', 'F4', 'C3', 'C4', 'P3', 'P4'];

  const channels: Record<string, number[]> = {};
  channelNames.forEach((ch) => {
    channels[ch] = Array.from({ length: numSamples }, (_, i) => {
      const t = i / sampleRate;
      // Simulate realistic EEG: alpha (10 Hz), theta (6 Hz), beta (20 Hz) + noise
      return (
        Math.sin(2 * Math.PI * 10 * t) * 15 +   // alpha band
        Math.sin(2 * Math.PI * 6 * t) * 10 +    // theta band
        Math.sin(2 * Math.PI * 20 * t) * 5 +    // beta band
        (Math.random() - 0.5) * 8               // noise
      );
    });
  });

  return {
    sample_rate: sampleRate,
    window_seconds: windowSeconds,
    channels,
    timestamp: new Date().toISOString(),
  };
}

// ─── Backend API Response Interface ──────────────────────────────────────────
interface AlzheimerAPIResponse {
  // Core detection result
  severity?: string;           // e.g. "Low" | "Mild" | "Moderate" | "Severe"
  severity_score?: number;     // 0–100 numeric score
  risk_score?: number;         // alternative field name
  risk_level?: string;         // alternative field name
  // Band power features (may be returned by backend)
  theta_alpha_ratio?: number;
  inter_hemispheric_coherence?: number;
  theta_power?: number;
  alpha_power?: number;
  delta_power?: number;
  // Additional metadata
  timestamp?: string;
  confidence?: number;
  message?: string;
}

// ─── Alzheimer's Risk Thresholds ──────────────────────────────────────────────
// Used as fallback when backend API is unavailable
function getRiskTier(thetaAlphaRatio: number, coherence: number): {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  score: number;
  description: string;
} {
  const thetaScore = Math.min(100, Math.max(0, ((thetaAlphaRatio - 0.4) / (2.8 - 0.4)) * 100));
  const coherenceScore = Math.min(100, Math.max(0, ((95 - coherence) / (95 - 45)) * 100));
  const combined = thetaScore * 0.55 + coherenceScore * 0.45;

  if (combined < 30) return {
    label: 'Stable Pattern',
    color: '#22d3ee',
    bgColor: 'rgba(34,211,238,0.08)',
    borderColor: 'rgba(34,211,238,0.3)',
    score: Math.round(combined),
    description: 'EEG biomarkers within expected cognitive range',
  };
  if (combined < 55) return {
    label: 'Pattern Shift',
    color: '#f59e0b',
    bgColor: 'rgba(245,158,11,0.08)',
    borderColor: 'rgba(245,158,11,0.3)',
    score: Math.round(combined),
    description: 'Mild biomarker variation — monitor trends over time',
  };
  if (combined < 75) return {
    label: 'Notable Variation',
    color: '#F97316',
    bgColor: 'rgba(249,115,22,0.08)',
    borderColor: 'rgba(249,115,22,0.3)',
    score: Math.round(combined),
    description: 'Slowing pattern detected — consider clinical review',
  };
  return {
    label: 'Significant Variation',
    color: '#ef4444',
    bgColor: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.35)',
    score: Math.round(combined),
    description: 'Significant biomarker deviation — specialist consultation advised',
  };
}

// Map backend severity string → risk tier object
function getRiskTierFromSeverity(
  severity: string,
  score: number,
  thetaAlphaRatio: number,
  coherence: number
): ReturnType<typeof getRiskTier> {
  const s = severity.toLowerCase();
  if (s.includes('low') || s.includes('normal') || s.includes('none')) {
    return { label: 'Stable Pattern', color: '#22d3ee', bgColor: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.3)', score, description: 'EEG biomarkers within expected cognitive range' };
  }
  if (s.includes('mild') || s.includes('moderate') || s.includes('early')) {
    return { label: 'Pattern Shift', color: '#f59e0b', bgColor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.3)', score, description: 'Mild biomarker variation — monitor trends over time' };
  }
  if (s.includes('elevated') || s.includes('medium') || s.includes('intermediate')) {
    return { label: 'Notable Variation', color: '#F97316', bgColor: 'rgba(249,115,22,0.08)', borderColor: 'rgba(249,115,22,0.3)', score, description: 'Slowing pattern detected — consider clinical review' };
  }
  if (s.includes('high') || s.includes('severe') || s.includes('advanced')) {
    return { label: 'Significant Variation', color: '#ef4444', bgColor: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.35)', score, description: 'Significant biomarker deviation — specialist consultation advised' };
  }
  // Fallback: derive from score
  return getRiskTier(thetaAlphaRatio, coherence);
}

// ─── Radial Arc Gauge ─────────────────────────────────────────────────────────
function ArcGauge({
  value,
  min,
  max,
  color,
  label,
  unit,
  warningThreshold,
  warningDirection = 'above',
  size = 96,
}: {
  value: number;
  min: number;
  max: number;
  color: string;
  label: string;
  unit: string;
  warningThreshold: number;
  warningDirection?: 'above' | 'below';
  size?: number;
}) {
  const pct = Math.min(1, Math.max(0, (value - min) / (max - min)));
  const r = (size / 2) * 0.72;
  const cx = size / 2;
  const cy = size / 2 + size * 0.06;
  const startAngle = -210;
  const sweepAngle = 240;
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const arcPath = (start: number, sweep: number, radius: number) => {
    const s = toRad(start);
    const e = toRad(start + sweep);
    const x1 = cx + radius * Math.cos(s);
    const y1 = cy + radius * Math.sin(s);
    const x2 = cx + radius * Math.cos(e);
    const y2 = cy + radius * Math.sin(e);
    const large = Math.abs(sweep) > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`;
  };

  const isWarning = warningDirection === 'above'
    ? value >= warningThreshold
    : value <= warningThreshold;

  const fillColor = isWarning ? '#F97316' : color;
  const strokeWidth = size * 0.075;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size * 0.88} viewBox={`0 0 ${size} ${size * 0.88}`}>
        <path
          d={arcPath(startAngle, sweepAngle, r)}
          fill="none"
          stroke="rgba(30,58,95,0.5)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {pct > 0 && (
          <path
            d={arcPath(startAngle, sweepAngle * pct, r)}
            fill="none"
            stroke={fillColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            style={{
              filter: `drop-shadow(0 0 ${size * 0.05}px ${fillColor}88)`,
              transition: 'stroke-dasharray 0.6s ease',
            }}
          />
        )}
        <text
          x={cx}
          y={cy + 4}
          textAnchor="middle"
          fill={fillColor}
          fontSize={size * 0.18}
          fontWeight="700"
          fontFamily="monospace"
        >
          {typeof value === 'number' ? (value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)) : '--'}
        </text>
        <text
          x={cx}
          y={cy + size * 0.18}
          textAnchor="middle"
          fill="rgba(139,175,212,0.7)"
          fontSize={size * 0.1}
        >
          {unit}
        </text>
      </svg>
      <span className="text-[10px] text-[#4A7AB5] font-semibold tracking-wide text-center leading-tight">{label}</span>
      {isWarning && (
        <span className="text-[9px] text-[#F97316] font-bold tracking-wide flex items-center gap-0.5">
          <AlertTriangle size={8} /> ELEVATED
        </span>
      )}
    </div>
  );
}

// ─── Risk Score Ring ──────────────────────────────────────────────────────────
function RiskRing({ score, color, size = 80 }: { score: number; color: string; size?: number }) {
  const r = (size / 2) * 0.78;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - score / 100);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(30,58,95,0.4)" strokeWidth={size * 0.09} />
      <circle
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke={color}
        strokeWidth={size * 0.09}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{
          filter: `drop-shadow(0 0 ${size * 0.06}px ${color}99)`,
          transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)',
        }}
      />
      <text x={cx} y={cy + 5} textAnchor="middle" fill={color} fontSize={size * 0.22} fontWeight="800" fontFamily="monospace">
        {score}
      </text>
    </svg>
  );
}

// ─── Typing Indicator ─────────────────────────────────────────────────────────
function TypingBars() {
  return (
    <div className="flex items-end gap-[3px] h-4">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="w-[3px] rounded-full bg-[#F97316]"
          style={{ animation: `alzTypingBar 1.1s ease-in-out infinite`, animationDelay: `${i * 0.12}s`, height: '100%' }}
        />
      ))}
    </div>
  );
}

// ─── AI Prompt Builder ────────────────────────────────────────────────────────
const ALZHEIMER_SYSTEM_PROMPT = `You are a clinical neuroscience expert specializing in EEG-based Alzheimer's disease biomarkers and early detection. 
Your role is to interpret EEG biomarker data and explain what it means for Alzheimer's risk assessment.
Reference published research on EEG biomarkers: theta/alpha slowing index, inter-hemispheric coherence, dominant frequency slowing, and connectivity loss.
Keep responses concise (3–4 short paragraphs), clinically grounded, and practically useful.
Use markdown with **bold** for key terms and • bullet points.
Always include a disclaimer that EEG alone is not diagnostic and clinical evaluation is required.
End with 1–2 actionable recommendations.`;

function buildAlzheimerPrompt(
  thetaAlphaRatio: number,
  coherence: number,
  thetaPower: number,
  alphaPower: number,
  deltaPower: number,
  riskScore: number,
  riskLabel: string,
  fromRealAPI: boolean
): string {
  const dataSource = fromRealAPI
    ? '(sourced from real-time EEG channel analysis via backend detection model)'
    : '(computed from live EEG band power metrics)';
  return `Analyze these EEG Alzheimer's risk biomarkers and provide a clinical neuroscience interpretation ${dataSource}:

**Alzheimer's Biomarker Readings:**
- Theta/Alpha Slowing Index: ${thetaAlphaRatio.toFixed(2)} (normal: 0.8–1.4; elevated ≥1.8 = significant slowing)
- Inter-Hemispheric Coherence: ${coherence.toFixed(0)}% (normal: ≥70%; reduced = connectivity loss)
- Theta Power: ${thetaPower.toFixed(1)} µV² (4–8 Hz; elevated in MCI/AD)
- Alpha Power: ${alphaPower.toFixed(1)} µV² (8–13 Hz; reduced in AD)
- Delta Power: ${deltaPower.toFixed(1)} µV² (0.5–4 Hz; elevated in moderate AD)
- Composite Risk Score: ${riskScore}/100 (${riskLabel})

Please explain:
1. What the theta/alpha slowing index reveals about cortical processing speed
2. What the inter-hemispheric coherence level indicates about neural connectivity
3. How the band power pattern compares to known Alzheimer's EEG signatures
4. The clinical significance of the composite risk score
5. Recommended next steps and important limitations of EEG-based screening`;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AlzheimerBiomarkerPanel() {
  const { metrics, status } = useSession();
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);

  const [displayedResponse, setDisplayedResponse] = useState('');
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [glowActive, setGlowActive] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const prevLoadingRef = useRef(false);
  const prevResponseRef = useRef('');
  const autoFetchRef = useRef(false);

  // ─── Real Backend Detection State ─────────────────────────────────────────
  const [apiDetectionResult, setApiDetectionResult] = useState<AlzheimerAPIResponse | null>(null);
  const [apiStatus, setApiStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [apiError, setApiError] = useState<string | null>(null);
  const apiIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const apiUrl = process.env.NEXT_PUBLIC_EEG_API_URL || '';

  // ─── Call Real Backend Alzheimer's Detection ───────────────────────────────
  const runBackendDetection = useCallback(async () => {
    if (!apiUrl) {
      setApiStatus('error');
      setApiError('NEXT_PUBLIC_EEG_API_URL not configured');
      return;
    }
    setApiStatus('loading');
    try {
      const payload = generateNamedChannelBatch();
      const res = await fetch(`${apiUrl}/alzheimer-detection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const json: AlzheimerAPIResponse = await res.json();
      setApiDetectionResult(json);
      setApiStatus('success');
      setApiError(null);
    } catch (err: unknown) {
      setApiStatus('error');
      setApiError(err instanceof Error ? err.message : 'Detection request failed');
      setApiDetectionResult(null);
    }
  }, [apiUrl]);

  // Start polling backend every 4 seconds when session is streaming
  useEffect(() => {
    if (status !== 'streaming' || !apiUrl) return;
    runBackendDetection();
    apiIntervalRef.current = setInterval(runBackendDetection, 4000);
    return () => {
      if (apiIntervalRef.current) clearInterval(apiIntervalRef.current);
    };
  }, [status, apiUrl, runBackendDetection]);

  // ─── Derive risk tier ─────────────────────────────────────────────────────
  // Prefer real API result; fall back to computed metrics
  const fromRealAPI = apiStatus === 'success' && apiDetectionResult !== null;

  const effectiveThetaAlpha = apiDetectionResult?.theta_alpha_ratio ?? metrics.thetaAlphaRatio;
  const effectiveCoherence = apiDetectionResult?.inter_hemispheric_coherence ?? metrics.interHemisphericCoherence;
  const effectiveThetaPower = apiDetectionResult?.theta_power ?? metrics.thetaPower;
  const effectiveAlphaPower = apiDetectionResult?.alpha_power ?? metrics.alphaPower;
  const effectiveDeltaPower = apiDetectionResult?.delta_power ?? metrics.deltaPower;

  const rawScore = apiDetectionResult?.severity_score ?? apiDetectionResult?.risk_score ?? null;
  const rawSeverity = apiDetectionResult?.severity ?? apiDetectionResult?.risk_level ?? null;

  const risk = (() => {
    if (fromRealAPI && rawSeverity !== null && rawScore !== null) {
      return getRiskTierFromSeverity(rawSeverity, Math.round(rawScore), effectiveThetaAlpha, effectiveCoherence);
    }
    if (fromRealAPI && rawScore !== null) {
      // Score only, no severity label
      const s = Math.round(rawScore);
      if (s < 30) return { label: 'Low Risk', color: '#22d3ee', bgColor: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.3)', score: s, description: 'EEG biomarkers within normal cognitive range' };
      if (s < 55) return { label: 'Moderate', color: '#f59e0b', bgColor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.3)', score: s, description: 'Mild biomarker deviation — monitor trends' };
      if (s < 75) return { label: 'Elevated', color: '#F97316', bgColor: 'rgba(249,115,22,0.08)', borderColor: 'rgba(249,115,22,0.3)', score: s, description: 'Notable slowing pattern — clinical review advised' };
      return { label: 'High Risk', color: '#ef4444', bgColor: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.35)', score: s, description: 'Significant biomarker deviation — specialist referral' };
    }
    return getRiskTier(effectiveThetaAlpha, effectiveCoherence);
  })();

  useEffect(() => { const t = setTimeout(() => setMounted(true), 120); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (error) toast.error('Alzheimer Biomarker AI: ' + error.message);
  }, [error]);

  useEffect(() => {
    if (isLoading && !prevLoadingRef.current) setGlowActive(true);
    if (!isLoading && prevLoadingRef.current) {
      const t = setTimeout(() => setGlowActive(false), 2000);
      prevLoadingRef.current = false;
      return () => clearTimeout(t);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && response && response !== prevResponseRef.current) {
      prevResponseRef.current = response;
      setDisplayedResponse(response);
    }
    if (isLoading && response) setDisplayedResponse(response);
  }, [response, isLoading]);

  const fetchInsights = useCallback(() => {
    const prompt = buildAlzheimerPrompt(
      effectiveThetaAlpha,
      effectiveCoherence,
      effectiveThetaPower,
      effectiveAlphaPower,
      effectiveDeltaPower,
      risk.score,
      risk.label,
      fromRealAPI
    );
    sendMessage(
      [
        { role: 'system', content: ALZHEIMER_SYSTEM_PROMPT },
        { role: 'user', content: prompt },
      ],
      { max_completion_tokens: 550 }
    );
    setLastFetchedAt(new Date());
    setInsightsOpen(true);
  }, [effectiveThetaAlpha, effectiveCoherence, effectiveThetaPower, effectiveAlphaPower, effectiveDeltaPower, risk.score, risk.label, fromRealAPI, sendMessage]);

  // Auto-fetch AI insights once when session starts streaming
  useEffect(() => {
    if (status === 'streaming' && !autoFetchRef.current) {
      autoFetchRef.current = true;
      const t = setTimeout(fetchInsights, 3200);
      return () => clearTimeout(t);
    }
  }, [status, fetchInsights]);

  return (
    <>
      <style>{`
        @keyframes alzTypingBar {
          0%, 100% { transform: scaleY(0.25); opacity: 0.4; }
          50% { transform: scaleY(1); opacity: 1; }
        }
        @keyframes alzPanelReveal {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes alzGlowPulse {
          0%, 100% { box-shadow: 0 0 10px 2px rgba(249,115,22,0.14), 0 0 0 1px rgba(249,115,22,0.1); }
          50% { box-shadow: 0 0 26px 6px rgba(249,115,22,0.32), 0 0 0 1px rgba(249,115,22,0.24); }
        }
        @keyframes riskPulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.04); }
        }
        .alz-panel-reveal { animation: alzPanelReveal 0.55s cubic-bezier(0.22,1,0.36,1) forwards; }
        .alz-glow-active { animation: alzGlowPulse 1.8s ease-in-out infinite; }
        .risk-pulse { animation: riskPulse 2.4s ease-in-out infinite; }
      `}</style>

      <div
        className={`rounded-xl border overflow-hidden transition-all duration-500 ${mounted ? 'alz-panel-reveal' : 'opacity-0'} ${glowActive ? 'border-[#F97316]/40 alz-glow-active' : 'border-[#1E3A5F]/60'}`}
        style={{ background: 'rgba(11,22,40,0.88)' }}
      >
        {/* ── Header ── */}
        <div className={`flex items-center justify-between px-5 py-3.5 border-b transition-all duration-500 ${glowActive ? 'border-[#F97316]/25 bg-[#060D1A]/80' : 'border-[#1E3A5F]/50 bg-[#060D1A]/60'}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg bg-[#0B1628] border flex items-center justify-center transition-all duration-500 ${glowActive ? 'border-[#F97316]/60' : 'border-[#F97316]/30'}`}>
              <Brain size={14} className="text-[#F97316]" />
            </div>
            <div>
              <h3 className="text-[13px] font-bold text-white tracking-wide">Alzheimer's Risk Biomarkers</h3>
              <p className="text-[10px] text-[#4A7AB5]">θ/α Slowing Index · Inter-Hemispheric Coherence · AI Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Backend API status badge */}
            <div className="flex items-center gap-1 px-2 py-1 rounded-md border border-[#1E3A5F]/50 bg-[#060D1A]/60">
              {apiStatus === 'loading' && <Loader2 size={10} className="text-[#F97316]/70 animate-spin" />}
              {apiStatus === 'success' && <Wifi size={10} className="text-emerald-400" />}
              {apiStatus === 'error' && <WifiOff size={10} className="text-red-400/70" />}
              {apiStatus === 'idle' && <Activity size={10} className="text-[#2D5A8E]" />}
              <span className="text-[9px] font-mono" style={{
                color: apiStatus === 'success' ? '#34d399' : apiStatus === 'error' ? '#f87171' : '#4A7AB5'
              }}>
                {apiStatus === 'success' ? 'LIVE API' : apiStatus === 'error' ? 'API ERR' : apiStatus === 'loading' ? 'DETECTING' : 'WAITING'}
              </span>
            </div>
            {isLoading && (
              <div className="flex items-center gap-1.5 mr-1">
                <TypingBars />
                <span className="text-[10px] text-[#F97316]/70 font-mono tracking-wide hidden sm:inline">Analyzing…</span>
              </div>
            )}
            {lastFetchedAt && !isLoading && (
              <span className="text-[10px] text-[#2D5A8E] hidden sm:inline">
                {lastFetchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <button
              onClick={fetchInsights}
              disabled={isLoading}
              title="Refresh AI analysis"
              className="w-7 h-7 rounded-lg bg-[#0B1628] border border-[#1E3A5F]/60 flex items-center justify-center hover:border-[#F97316]/40 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={12} className={`text-[#4A7AB5] ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="p-5">

          {/* API Detection Result Banner */}
          {fromRealAPI && (rawSeverity || rawScore !== null) && (
            <div className="mb-4 flex items-center gap-3 px-4 py-2.5 rounded-lg border"
              style={{ background: risk.bgColor, borderColor: risk.borderColor }}>
              <Wifi size={12} style={{ color: risk.color }} />
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-bold" style={{ color: risk.color }}>
                  Backend Detection: {rawSeverity ?? 'Score ' + rawScore}
                </span>
                {apiDetectionResult?.confidence !== undefined && (
                  <span className="ml-2 text-[10px] text-[#4A7AB5]">
                    Confidence: {(apiDetectionResult.confidence * 100).toFixed(0)}%
                  </span>
                )}
              </div>
              <span className="text-[9px] font-mono text-[#2D5A8E] shrink-0">
                /alzheimer-detection
              </span>
            </div>
          )}

          {/* API error notice (non-blocking) */}
          {apiStatus === 'error' && apiError && (
            <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/20 bg-red-500/05">
              <WifiOff size={11} className="text-red-400/70 shrink-0" />
              <span className="text-[10px] text-red-400/70 font-mono truncate">
                Backend unavailable — showing computed metrics. ({apiError})
              </span>
            </div>
          )}

          {/* Top row: gauges + risk score */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            {/* Gauges */}
            <div className="flex items-center gap-6 flex-wrap">
              <ArcGauge
                value={effectiveThetaAlpha}
                min={0.4}
                max={2.8}
                color="#8b5cf6"
                label="θ/α Slowing Index"
                unit="ratio"
                warningThreshold={1.8}
                warningDirection="above"
                size={100}
              />
              <ArcGauge
                value={effectiveCoherence}
                min={45}
                max={95}
                color="#22d3ee"
                label="Inter-Hemispheric Coherence"
                unit="%"
                warningThreshold={60}
                warningDirection="below"
                size={100}
              />
              <ArcGauge
                value={effectiveAlphaPower}
                min={8}
                max={38}
                color="#22d3ee"
                label="Alpha Power"
                unit="µV²"
                warningThreshold={14}
                warningDirection="below"
                size={88}
              />
              <ArcGauge
                value={effectiveThetaPower}
                min={6}
                max={42}
                color="#8b5cf6"
                label="Theta Power"
                unit="µV²"
                warningThreshold={28}
                warningDirection="above"
                size={88}
              />
            </div>

            {/* Risk score ring */}
            <div
              className="flex flex-col items-center gap-2 px-5 py-4 rounded-xl border risk-pulse"
              style={{ background: risk.bgColor, borderColor: risk.borderColor }}
            >
              <RiskRing score={risk.score} color={risk.color} size={76} />
              <div className="text-center">
                <div className="text-[13px] font-bold" style={{ color: risk.color }}>{risk.label}</div>
                <div className="text-[10px] text-[#4A7AB5] mt-0.5 max-w-[130px] text-center leading-tight">{risk.description}</div>
                <div className="text-[9px] mt-1 font-mono" style={{ color: fromRealAPI ? '#34d399' : '#2D5A8E' }}>
                  {fromRealAPI ? '● REAL API' : '○ COMPUTED'}
                </div>
              </div>
            </div>
          </div>

          {/* Reference thresholds bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
            {[
              { label: 'θ/α Normal', range: '0.8 – 1.4', current: effectiveThetaAlpha.toFixed(2), warn: effectiveThetaAlpha >= 1.8 },
              { label: 'θ/α Elevated', range: '≥ 1.8', current: null, warn: false },
              { label: 'Coherence Normal', range: '≥ 70%', current: `${effectiveCoherence.toFixed(0)}%`, warn: effectiveCoherence < 60 },
              { label: 'Coherence Low', range: '< 60%', current: null, warn: false },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-lg px-3 py-2 border"
                style={{
                  background: item.warn ? 'rgba(249,115,22,0.06)' : 'rgba(30,58,95,0.15)',
                  borderColor: item.warn ? 'rgba(249,115,22,0.25)' : 'rgba(30,58,95,0.4)',
                }}
              >
                <div className="text-[9px] text-[#4A7AB5] font-semibold uppercase tracking-wide mb-0.5">{item.label}</div>
                <div className="text-[11px] font-mono font-bold" style={{ color: item.warn ? '#F97316' : '#8BAFD4' }}>
                  {item.current ?? item.range}
                </div>
              </div>
            ))}
          </div>

          {/* AI Insights collapsible */}
          <div className="border border-[#1E3A5F]/50 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setInsightsOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-[#060D1A]/60 hover:bg-[#060D1A]/80 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Zap size={13} className="text-[#F97316]" />
                <span className="text-[12px] font-bold text-white tracking-wide">AI Biomarker Interpretation</span>
                {isLoading && <TypingBars />}
              </div>
              {insightsOpen ? <ChevronUp size={13} className="text-[#4A7AB5]" /> : <ChevronDown size={13} className="text-[#4A7AB5]" />}
            </button>

            <div className={`transition-all duration-300 overflow-hidden ${insightsOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
              <div className="px-4 py-4 border-t border-[#1E3A5F]/40">
                {isLoading && !displayedResponse && (
                  <div className="flex flex-col gap-2">
                    {[80, 65, 90, 55].map((w, i) => (
                      <div key={i} className="h-3 rounded-full bg-[#1E3A5F]/40 animate-pulse" style={{ width: `${w}%` }} />
                    ))}
                  </div>
                )}
                {displayedResponse ? (
                  <div className="prose prose-invert prose-sm max-w-none text-[12px] leading-relaxed text-[#8BAFD4]
                    [&_strong]:text-white [&_strong]:font-semibold
                    [&_p]:mb-2 [&_p]:text-[#8BAFD4]
                    [&_ul]:my-1.5 [&_li]:text-[#8BAFD4] [&_li]:mb-0.5
                    [&_h1]:text-white [&_h2]:text-white [&_h3]:text-[#4A7AB5]">
                    <ReactMarkdown>{displayedResponse}</ReactMarkdown>
                    {isLoading && <span className="inline-block w-1.5 h-3.5 bg-[#F97316] ml-0.5 animate-pulse rounded-sm" />}
                  </div>
                ) : !isLoading ? (
                  <div className="flex flex-col items-center gap-3 py-4 text-center">
                    <Activity size={24} className="text-[#1E3A5F]" />
                    <p className="text-[11px] text-[#2D5A8E]">Click the refresh button to generate AI biomarker analysis</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-[9px] text-[#2D5A8E] mt-3 leading-relaxed">
            ⚠ EEG biomarkers are research indicators only. This panel does not constitute a medical diagnosis. Consult a qualified neurologist for clinical evaluation.
          </p>
        </div>
      </div>
    </>
  );
}
